<template>
  <div class="w-full">
    <div
      class="p-3 w-full border border-border-normal data-card-row"
      :style="isExpanded ? 'border-radius: 16px 16px 0 0' : 'border-radius: 16px'"
    >
      <div class="flex items-center ">
        <!-- Connection owner -->
        <div
          class="flex items-center gap-2 w-full"
          style="max-width: 160px"
        >
          <img
            v-if="dataSource?.created_by?.avatar"
            :src="dataSource?.created_by?.avatar"
            alt="profile image"
            class="w-6 h-6 rounded-full"
          >
          <img
            v-else
            src="../../assets/images/defaultProfileImage.webp"
            alt="default profile image"
            class="w-6 h-6 rounded-full"
          >
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted capitalize truncate"
          >
            {{ dataSource?.created_by?.name }}
          </BaseText>
        </div>
        <!-- Total accounts -->
      </div>
      <div class=" flex items-center gap-2">
        <BaseText
          size="sm"
          class="text-text-normal"
        >
          Accounts
        </BaseText>
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted"
        >
          {{ dataSource.connections?.length ?? 0 }}
        </BaseText>
      </div>
      <div class="flex items-center flex-nowrap gap-2 ">
        <div class="rounded-md flex items-center gap-1 px-1.5 py-1 max-h-6 bg-secondary-green-10">
          <ConnectionIcon
            class="text-secondary-green-300"
            :width="15"
            :height="15"
          />
          <BaseText
            type="label"
            size="xs"
            class="text-secondary-green-300 whitespace-nowrap"
          >
            {{ accountStatusCount.valid }} Live
          </BaseText>
        </div>
        <!-- v-if="accountStatusCount.invalid > 0" -->
        <div
          class="rounded-md flex items-center gap-1 px-1.5 py-1 max-h-6 bg-secondary-yellow-10"
        >
          <ConnectionIcon
            class="text-secondary-yellow-300"
            :width="15"
            :height="15"
          />
          <BaseText
            type="label"
            size="xs"
            class="text-secondary-yellow-300 whitespace-nowrap"
          >
            {{ accountStatusCount.invalid }} Issues
          </BaseText>
        </div>
      </div>
      <div class="flex items-center justify-self-end gap-2 ">
        <!-- Edit connection -->
        <button
          class="base-btn flex items-center gap-1.5 rounded-md transition-colors  py-1.5 px-2 flex-nowrap"
          :disabled="!isOwner"
          @click="$emit('editConnection')"
        >
          <EditSquareIcon class="text-icon-normal" />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted whitespace-nowrap"
          >
            Manage
          </BaseText>
        </button>
        <button
          class="base-btn p-1.5 rounded-md transition-colors "
          @click="isExpanded = !isExpanded"
        >
          <ChevronIcon
            class="transition-transform transform text-text-subdued"
            :style="isExpanded ? 'transform: scale(1, -1)' : ''"
          />
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isExpanded"
        class="border-l border-r border-b border-border-normal rounded-b-2xl"
      >
        <div class="p-3 space-y-2">
          <div
            v-for="(account, accountIndex) in sourceAdAccounts"
            :key="account.accountId"
          >
            <div class="account-grid">
              <div class="flex items-center gap-2">
                <div
                  class="w-5 h-5 text-center text-text-muted p-0.5 rounded-md bg-background-normal"
                >
                  <BaseText
                    type="label"
                    size="xs"
                  >
                    {{ accountIndex + 1 }}
                  </BaseText>
                </div>
                <BaseText
                  type="label"
                  size="sm"
                  class="text-text-muted truncate"
                >
                  {{ account.account_name }}
                </BaseText>
              </div>
              <BaseText
                type="label"
                size="sm"
                class="text-text-subdued whitespace-nowrap"
              >
                {{ account.account_id }}
              </BaseText>
              <div class="flex items-center gap-2">
                <div
                  v-if="account.lens"
                  class="bg-background-normal rounded-md flex items-center gap-1 p-0.5 truncate"
                  :class="{'pr-1.5': !account.lens?.avatar}"
                >
                  <img
                    v-if="account.lens?.avatar"
                    :src="account.lens.avatar"
                    class="object-cover rounded-md w-5 h-5"
                  >
                  <div
                    v-else
                    class="w-5 h-5 flex items-center justify-center rounded-md bg-neutral-400 p-0.5"
                  >
                    <BaseText
                      type="label"
                      size="xs"
                      class="text-white"
                    >
                      {{ getInitials(account.lens.name) }}
                    </BaseText>
                  </div>
                  <BaseText
                    type="label"
                    size="xs"
                    class="text-text-muted truncate"
                  >
                    {{ account.lens.name }}
                  </BaseText>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ConnectionIcon from '../globals/Icons/ConnectionIcon.vue'
import EditSquareIcon from '../globals/Icons/EditSquareIcon.vue'
import ChevronIcon from '../globals/Icons/ChevronIcon.vue'

export default {
  name: 'DataSourceCard',
  components: {
    ConnectionIcon,
    EditSquareIcon,
    ChevronIcon
  },
  props: {
    dataSource: {
      type: Object,
      required: true
    },
    isOwner: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      isExpanded: true
    }
  },
  computed: {
    sourceAdAccounts () {
      return this.dataSource.connections
    },
    accountStatusCount () {
      const validAdAccounts = this.sourceAdAccounts.filter(account => account.is_valid)
      return { valid: validAdAccounts.length, invalid: this.sourceAdAccounts.length - validAdAccounts.length }
    }
  },
  methods: {
    getInitials (name) {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    }
  }
}
</script>

<style scoped>
.account-grid {
  display: grid;
  grid-template-columns: 200px 200px 1fr;
  align-items: center;
}

.base-btn {
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
}

.base-btn:hover:not(:disabled) {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  /* box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17), 0px 0px 0px 1px rgba(0, 56, 108, 0.12); */
}
.base-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 50ms;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave-from {
  opacity: 1;
}

.data-card-row {
  display: grid;
  grid-template-columns: 200px 200px 98px 1fr;
  align-items: center;
}
</style>
